import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from "@mui/material";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { useEffect, useState } from "react";
import { useCreateSetupIntent } from "@/hooks/usePaymentMethods";
import { usePublicConfig } from "@/hooks/usePublicConfig";
import { getStripePromise } from "@/utils/stripe-helper";

// Off-session cards need a mandate. This is the disclosure we SHOW and record; nothing is sent
// to Stripe (cards get no auto-rendered mandate text, unlike bank debits). See design §3.6 —
// wording is a placeholder until the auto-top-up rule is finalised.
const CONSENT_TEXT =
  "I authorize Gifts for Good to securely save this card and charge it automatically to top up " +
  "based on the terms for the group's active campaigns.";

/** The card form — must live inside <Elements> to use the Stripe hooks. */
function CardForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const [consented, setConsented] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);

    // redirect: "if_required" — a 3DS card opens Stripe's own modal (no page redirect); a plain
    // card resolves in place. The webhook is what actually saves the card; this is the trigger.
    const { error: confirmError } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message ?? "Couldn't save the card. Please try again.");
      setSubmitting(false);
      return;
    }
    onDone();
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
    >
      {/* wallets.link:"never" disables Stripe Link — a personal cross-merchant wallet that would
          autofill the clicking admin's OWN card onto the group. Link is a client-side wallet, so
          this must be set here on the Element (the intent's payment_method_types can't suppress
          passive Link). Card fields only. */}
      <PaymentElement
        options={{ wallets: { link: "never", applePay: "never", googlePay: "never" } }}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            size="small"
          />
        }
        label={
          <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
            {CONSENT_TEXT}
          </Typography>
        }
      />

      {error && (
        <Alert severity="error" variant="outlined" sx={{ fontSize: 13 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
        <Button onClick={onCancel} disabled={submitting} sx={{ color: "var(--color-graphite)" }}>
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={!stripe || !consented || submitting}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {submitting ? "Saving…" : "Save card"}
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Add-a-card modal. Mints a SetupIntent for the group, hosts Stripe's Payment Element, and
 * confirms with no page redirect. The card is not in our list until the setup_intent.succeeded
 * webhook writes it — locally that needs `stripe listen` forwarding to the API.
 */
export default function AddPaymentMethodModal({
  open,
  groupId,
  onClose,
  onAdded,
}: {
  open: boolean;
  groupId: number;
  onClose: () => void;
  onAdded: () => void;
}) {
  // mutate is referentially stable across renders, so depending on it won't re-mint.
  const { mutate: mintSetupIntent } = useCreateSetupIntent(groupId);
  // Publishable key from the API (cached for the session). Only when present can we mount Stripe.
  const { data: config } = usePublicConfig();
  const stripePromise = config?.stripePublishableKey
    ? getStripePromise(config.stripePublishableKey)
    : null;
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Mint a fresh SetupIntent each time the modal opens (once config has resolved); reset on close.
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setError(null);
      return;
    }
    if (config && !config.stripePublishableKey) {
      setError("Payments aren't configured (missing publishable key).");
      return;
    }
    if (!stripePromise) return; // config still loading — wait
    mintSetupIntent(undefined, {
      onSuccess: (secret) => setClientSecret(secret),
      onError: (e) => setError(e instanceof Error ? e.message : "Couldn't start adding a card."),
    });
  }, [open, config, stripePromise, mintSetupIntent]);

  function handleDone() {
    onAdded();
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 24 }}>
        Add payment method
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 2 }}>
          Your card is processed securely by Stripe. We never see or store the card number.
        </Typography>

        {error ? (
          <Alert severity="error" variant="outlined" sx={{ fontSize: 13 }}>
            {error}
          </Alert>
        ) : clientSecret && stripePromise ? (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CardForm onDone={handleDone} onCancel={onClose} />
          </Elements>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress aria-label="Loading" size={24} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
